// ts-rest contract for the Suitability Records tier (three-tier client model
// Stage 2, ADR-0015).
//
// One area over the SUR mandate-container header (`SuitabilityRecord`,
// desk/ops/comply/view) — a per-view presentation projection, NOT a domain
// model. THE COMPLIANCE WALL RIDES THE PAYLOAD SHAPE (D5): the detail
// response carries a nullable `assessmentContent` block that the controller
// populates ONLY when the caller's `ctx.read(SuitabilityRecordAssessment)`
// succeeds. Desk and Ops receive the header with the block ABSENT (null);
// Comply receives the current content (latest by createdOn, answer #13).
// The content type itself never gets its own read endpoint outside this
// comply-gated block.
//
// Endpoints under /api/suitability-records:
//   GET   /api/suitability-records                    — directory (search + status filter)
//   GET   /api/suitability-records/:recordId          — detail (+ comply-only content block)
//   POST  /api/suitability-records                    — create (reference is SERVER-allocated, D3)
//   PATCH /api/suitability-records/:recordId          — edit descriptive block
//   POST  /api/suitability-records/:recordId/status   — lifecycle (activate/close/reopen)
//   POST  /api/suitability-records/:recordId/approval — comply sign-off (approve/withdraw)
//   POST  /api/suitability-records/:recordId/owner    — ops-mediated book move
//   GET   /api/suitability-records/:recordId/client-candidates — the record's own clients
//   POST  /api/suitability-records/with-fact-find              — a new SUR, its links and its Fact Find (SEP-353)
//   POST  /api/suitability-records/:recordId/internal-review   — create + link an Internal Review

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodyWithCode as errorBody } from '../schemas/common';
import { contactNameProjectionSchema, type ContactNameProjection } from '../contact-name';
import { matchesAllTokens } from '../token-search';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums (closed domain values — snake_case VALUES, camelCase keys)
// ---------------------------------------------------------------------------

export const suitabilityRecordStatusSchema = z.enum(['draft', 'active', 'closed']);
export type SuitabilityRecordStatus = z.infer<typeof suitabilityRecordStatusSchema>;

export const suitabilityRecordAssessmentTypeSchema = z.enum(['individual', 'entity']);
export type SuitabilityRecordAssessmentType = z.infer<typeof suitabilityRecordAssessmentTypeSchema>;

/**
 * Entity Type (SEP-186) — closed vocabulary, replacing the free text that
 * shipped with ADR-0015 Stage 2. Mirrors
 * `SUITABILITY_RECORD_ENTITY_TYPES` in `@septima/ontology-wealth` (the domain
 * statement); a server-side parity test pins the two together, because the
 * contract may not import the ontology.
 */
export const suitabilityRecordEntityTypeSchema = z.enum(['trust', 'corporate', 'charity', 'other']);
export type SuitabilityRecordEntityTypeValue = z.infer<typeof suitabilityRecordEntityTypeSchema>;

/**
 * Which entity-identity fields an Entity Type carries (SEP-186). Mirrors
 * `ENTITY_TYPE_CONDITIONAL_FIELDS` in the ontology so the form renders exactly
 * the fields the Action will accept; the parity test keeps them equal.
 */
export const ENTITY_TYPE_CONDITIONAL_FIELDS: Readonly<
    Record<SuitabilityRecordEntityTypeValue, readonly EntityIdentityConditionalField[]>
> = {
    trust: ['legalEntityIdentifier'],
    corporate: ['legalEntityIdentifier', 'entityRegistrationNumber'],
    charity: ['charityNumber'],
    other: ['entityTypeOther'],
};

export const ENTITY_IDENTITY_CONDITIONAL_KEYS = [
    'entityTypeOther',
    'legalEntityIdentifier',
    'entityRegistrationNumber',
    'charityNumber',
] as const;

export type EntityIdentityConditionalField = (typeof ENTITY_IDENTITY_CONDITIONAL_KEYS)[number];

/** The conditional fields to render for this Entity Type; none until one is picked. */
export function entityIdentityConditionalFields(
    entityType: SuitabilityRecordEntityTypeValue | null | undefined,
): readonly EntityIdentityConditionalField[] {
    if (!entityType) return [];
    return ENTITY_TYPE_CONDITIONAL_FIELDS[entityType];
}

/** Field labels, so every surface names these the same way. */
export const ENTITY_IDENTITY_FIELD_LABELS: Readonly<
    Record<EntityIdentityConditionalField, string>
> = {
    entityTypeOther: 'Entity type (other)',
    legalEntityIdentifier: 'Legal entity identifier',
    entityRegistrationNumber: 'Registration number',
    charityNumber: 'Charity number',
};

export const suitabilityRecordLifecycleActionSchema = z.enum(['activate', 'close', 'reopen']);
export type SuitabilityRecordLifecycleAction = z.infer<
    typeof suitabilityRecordLifecycleActionSchema
>;

export const suitabilityRecordApprovalActionSchema = z.enum(['approve', 'withdraw']);
export type SuitabilityRecordApprovalAction = z.infer<typeof suitabilityRecordApprovalActionSchema>;

// ---------------------------------------------------------------------------
// Directory rows + stats
// ---------------------------------------------------------------------------

/**
 * The SUR's primary contact — the person the record is *about* (ADR-0015 D7 /
 * PRD §9.4: the linked contact whose role carries the lowest
 * `display_priority`, ties broken by the earliest link). Resolved server-side
 * from the relationship tier in one set read; a SUR row never carries a
 * per-row join.
 *
 * It composes {@link contactNameProjectionSchema} rather than respelling the
 * pair, because the directory shows the SAME two strings the Contacts
 * directory does: the surname alone (its own sortable column) and the full
 * display name.
 *
 * Both names are nullable independently of `contactId`: a contact outside the
 * caller's read scope keeps its id and loses its names, exactly as
 * `involvedClients` does on the clients tier (ADR-0005). Treat a present
 * `contactId` with null names as "withheld", never as "no contact".
 */
export const suitabilityRecordPrimaryContactSchema = contactNameProjectionSchema.extend({
    contactId: z.string(),
    /** Catalogue LABEL of the contact-end role (e.g. "Sole Owner"), or null
     *  when the catalogue no longer carries that role. Never the role key. */
    roleLabel: z.string().nullable(),
});
export type SuitabilityRecordPrimaryContact = z.infer<typeof suitabilityRecordPrimaryContactSchema>;

/** The tiers a suitability record links to. Structural, not a role. */
export const suitabilityRecordEndpointTypeSchema = z.enum(['contact', 'account']);
export type SuitabilityRecordEndpointType = z.infer<typeof suitabilityRecordEndpointTypeSchema>;

/**
 * A party the record touches — one of its linked contacts, or one of its
 * linked Accounts. This is the suitability-record tier's answer to
 * the clients tier's `involvedClients` (`clients.ts`): a SUR "may link to
 * multiple clients", and a picker option that names none of them is the whole
 * of SEP-180. The label builder below is the intended consumer.
 *
 * TWO RULES, AND THE SECOND IS A DELIBERATE DEPARTURE FROM `involvedClients`.
 *
 * 1. **Names resolve only within the caller's read scope** (ADR-0005). The
 *    server resolves every party through the caller's own `ctx.read`, so a
 *    contact or Account in another IM's book simply does not resolve. The
 *    privacy decision is made server-side; no consumer may re-derive it.
 *
 * 2. **A party the caller cannot read is not on the wire at all.** Where
 *    `involvedClients` keeps the entry and nulls only `clientName`,
 *    this array omits the entry entirely — hence the NON-nullable
 *    `endpointName`. `involvedClients` retains those ids for its single-owner
 *    back-compatibility path and predates the ADR-0005 sharpening; a new
 *    field inherits no such debt, and the retained-id shape is a live trap:
 *    an overflow badge written as `list.length - shown` discloses a COUNT of
 *    another IM's clients to a book-scoped caller, which ADR-0005 treats as
 *    disclosing firm state. Nothing can navigate to, name, or link a party it
 *    may not read, so the id buys nothing and the count costs something.
 *    Every entry here is therefore visible by construction, and `+N` computed
 *    off this array is safe — the trap is unrepresentable rather than merely
 *    avoided.
 *
 * A consequence worth stating: a SUR whose only links are out of scope
 * arrives with an EMPTY array, indistinguishable from a SUR that links to
 * nothing. That is intended. `hasLinkedContact` answers "does it link?"
 * without naming or counting anything.
 */
export const suitabilityRecordInvolvedPartySchema = z.object({
    endpointType: suitabilityRecordEndpointTypeSchema,
    endpointId: z.string(),
    /** Contact display name, or the Account's parent code / client reference.
     *  Never null — an unreadable party is omitted, not blanked (see above). */
    endpointName: z.string(),
    /** Contact surname when the tier has one; null for an Account. */
    lastName: z.string().nullable(),
});
export type SuitabilityRecordInvolvedParty = z.infer<typeof suitabilityRecordInvolvedPartySchema>;

export const suitabilityRecordRowSchema = z.object({
    recordId: z.string(),
    /** SUR-#### — server-allocated (D3), unique. */
    reference: z.string(),
    assessmentType: suitabilityRecordAssessmentTypeSchema,
    status: suitabilityRecordStatusSchema,
    /** Mirrored band (D5) — a real header field, never read through FactFind. */
    riskProfileBand: z.string().nullable(),
    ownerImUserId: z.string(),
    ownerImName: z.string().nullable(),
    inceptionDate: z.string().nullable(),
    nextReviewDate: z.string().nullable(),
    approved: z.boolean(),
    /** For entity records, the entity name; null for individuals. */
    entityName: z.string().nullable(),
    /** False for every row until the Relationship tier links (Stage 4);
     * backs the PRD §11 "No linked Contact" filter chip. */
    hasLinkedContact: z.boolean(),
    /** The record's primary contact, or null when no linked contact carries a
     * ranked role. Backs the directory's Last name / Full name columns
     * (SEP-175). Distinguish the two empties: `null` is "no linked contact",
     * a present entry with null names is "outside your read scope". */
    primaryContact: suitabilityRecordPrimaryContactSchema.nullable(),
    /** Every party the record touches that the caller may read — its linked
     * contacts and Accounts (SEP-180). Contacts first. Empty when the
     * record links to nothing, and also when everything it links to is out of
     * scope; see {@link suitabilityRecordInvolvedPartySchema} for why those two
     * are deliberately the same on the wire. */
    involvedParties: z.array(suitabilityRecordInvolvedPartySchema),
});
export type SuitabilityRecordRow = z.infer<typeof suitabilityRecordRowSchema>;

// ---------------------------------------------------------------------------
// The one-line record label (SEP-180)
// ---------------------------------------------------------------------------

/** Segment separator for the one-line record label. */
export const SUITABILITY_RECORD_LABEL_SEPARATOR = ' · ';

/** Party references shown before the label starts counting. Two keeps a
 *  `<select>` option readable while still showing a joint Account pair. */
export const SUITABILITY_RECORD_LABEL_MAX_REFS = 2;

/**
 * The pieces of a record's one-line label, resolved but not yet joined.
 * Exposed separately from {@link suitabilityRecordPickerLabel} so a surface
 * that wants to style the segments (a two-line option, a subdued reference)
 * does not re-derive the anchor rule, and so the search-field set can be
 * built from the SAME derivation — a token you can see must be a token you
 * can search.
 */
export interface SuitabilityRecordLabelParts {
    /** Always present: SUR-####. */
    readonly reference: string;
    /** The best available human anchor, or null for a person-less record. */
    readonly anchor: string | null;
    /** Linked Account references, capped. */
    readonly visibleRefs: readonly string[];
    /** How many further readable references were not shown. NEVER a count of
     *  parties the caller cannot read — those never reach the wire. */
    readonly overflowCount: number;
}

/** What {@link suitabilityRecordAnchor} needs to know about a record. Narrower
 *  than {@link SuitabilityRecordRow} on purpose: a server surface that composes
 *  the anchor from ontology rows (the book-level relationships register, the
 *  per-record panel) has no DTO row to hand, and must not fork the rule. */
export interface SuitabilityRecordAnchorParts {
    /** The §9.4 primary contact's name projection, or null. Names are null when
     *  the contact is outside the caller's read scope. */
    readonly primaryContact?: ContactNameProjection | null;
    readonly entityName?: string | null;
    /** Other READABLE linked contacts, best candidate first. */
    readonly linkedContacts?: ReadonlyArray<ContactNameProjection>;
}

/**
 * **The anchor rule** — the human half of a record's label, best first. The
 * point is that an INDIVIDUAL record (742 of 901 in prod) must not fall
 * through to a bare `SUR-1234`:
 *
 *   1. the primary contact's FULL name — the whole person, the same string the
 *      directory's Full name column shows;
 *   2. their surname, when no full name could be derived;
 *   3. the entity name, for an entity record (an entity has no surname);
 *   4. any other readable linked contact, when no linked contact carried a
 *      role the catalogue ranks — a name beats a bare reference. Reachable
 *      whenever an admin adds a `contact_suitability` role with no
 *      `display_priority` (D4/§12 names IFA, Settlor and Deputy as runtime
 *      additions), which the §9.4 resolver then declines to rank;
 *   5. null. A joint Account resolves to no single person, and a record whose
 *      only parties are in another book resolves to nobody the caller may
 *      name. The label then carries reference + references and no person, and
 *      that is a correct label, not a degraded one.
 *
 * The surname led steps 1 and 4 until the firm asked for the whole name
 * (SEP-188). A surname is the shortest thing that identifies a person, but it
 * identifies a FAMILY on a surface built to show families: the relationship
 * register puts a record beside the contacts it links to, and a household of
 * Pryces reads as one word repeated. The full name is what the Contact tier
 * beside it already shows, so the two tiers now name a person the same way.
 * Nothing is lost — the reference still separates two records of one person,
 * and the surname stays searchable (see below).
 *
 * EVERY surface that names a suitability record calls this — the picker label
 * below, the book-level relationships register and the per-record relationships
 * panel — so the same record can never read two ways in two places.
 */
export function suitabilityRecordAnchor(parts: SuitabilityRecordAnchorParts): string | null {
    const fallback = parts.linkedContacts?.[0];
    return (
        parts.primaryContact?.fullName ??
        parts.primaryContact?.lastName ??
        parts.entityName ??
        fallback?.fullName ??
        fallback?.lastName ??
        null
    );
}

/**
 * Resolve the label pieces for a record — the reference, the
 * {@link suitabilityRecordAnchor}, and the capped linked-Account references.
 */
export function suitabilityRecordLabelParts(
    row: SuitabilityRecordRow,
    maxRefs: number = SUITABILITY_RECORD_LABEL_MAX_REFS,
): SuitabilityRecordLabelParts {
    const anchor = suitabilityRecordAnchor({
        primaryContact: row.primaryContact,
        entityName: row.entityName,
        linkedContacts: row.involvedParties
            .filter((p) => p.endpointType === 'contact')
            .map((p) => ({ lastName: p.lastName, fullName: p.endpointName })),
    });

    const refs = row.involvedParties
        .filter((p) => p.endpointType === 'account')
        .map((p) => p.endpointName);

    return {
        reference: row.reference,
        anchor,
        visibleRefs: refs.slice(0, Math.max(0, maxRefs)),
        overflowCount: Math.max(0, refs.length - Math.max(0, maxRefs)),
    };
}

/**
 * The record as one line — `SUR-1005 · Samuel Ashford · 2620-D 0NNNNN +2`.
 *
 * Reference, then the human anchor, then the linked Account
 * references with an overflow count. Segments that resolve to nothing are
 * dropped rather than rendered as an empty gap, so the person-less and
 * reference-less cases degrade to `SUR-1005 · 2620-D 0NNNNN` and `SUR-1005`.
 *
 * The `+N` is computed from `involvedParties`, which carries ONLY parties the
 * caller may read (see {@link suitabilityRecordInvolvedPartySchema}) — it can
 * never disclose how many of another IM's clients a record touches.
 */
export function suitabilityRecordPickerLabel(
    row: SuitabilityRecordRow,
    maxRefs: number = SUITABILITY_RECORD_LABEL_MAX_REFS,
): string {
    const parts = suitabilityRecordLabelParts(row, maxRefs);
    const refs =
        parts.visibleRefs.length === 0
            ? null
            : parts.overflowCount > 0
              ? `${parts.visibleRefs.join(SUITABILITY_RECORD_LABEL_SEPARATOR)} +${parts.overflowCount}`
              : parts.visibleRefs.join(SUITABILITY_RECORD_LABEL_SEPARATOR);

    return [parts.reference, parts.anchor, refs]
        .filter((segment): segment is string => Boolean(segment))
        .join(SUITABILITY_RECORD_LABEL_SEPARATOR);
}

/**
 * The fields a Suitability Records search looks at, in one place so the
 * server-side filter and any client-side filter can never disagree about which
 * rows a query matches. Feed it to `matchesAllTokens` — the match is token-AND,
 * so "richards SUR-1005" finds the record by surname AND reference.
 *
 * It is a strict SUPERSET of what {@link suitabilityRecordPickerLabel} renders,
 * and that direction is the invariant (pinned by test): a token on screen is
 * always searchable, while a party the label had to truncate stays findable.
 * Half of SEP-180 is that the picker only searched the reference — the code an
 * IM has never memorised.
 */
export function suitabilityRecordRowSearchFields(
    row: SuitabilityRecordRow,
): ReadonlyArray<string | null> {
    return [
        row.reference,
        row.riskProfileBand,
        row.ownerImName,
        row.entityName,
        row.primaryContact?.lastName ?? null,
        row.primaryContact?.fullName ?? null,
        ...row.involvedParties.flatMap((party) => [party.endpointName, party.lastName]),
    ];
}

/** True when every whitespace-separated token of `search` appears somewhere in
 *  the row's searchable text. An empty query matches every row. */
export function suitabilityRecordMatchesSearch(
    row: SuitabilityRecordRow,
    tokens: readonly string[],
): boolean {
    return matchesAllTokens(tokens, suitabilityRecordRowSearchFields(row));
}

export const suitabilityRecordsStatsSchema = z.object({
    total: z.number().int(),
    drafts: z.number().int(),
    active: z.number().int(),
    approved: z.number().int(),
});
export type SuitabilityRecordsStats = z.infer<typeof suitabilityRecordsStatsSchema>;

export const listSuitabilityRecordsResponseSchema = z.object({
    rows: z.array(suitabilityRecordRowSchema),
    stats: suitabilityRecordsStatsSchema,
});
export type ListSuitabilityRecordsResponse = z.infer<typeof listSuitabilityRecordsResponseSchema>;

// ---------------------------------------------------------------------------
// Detail (+ the comply-only content block)
// ---------------------------------------------------------------------------

/**
 * The comply-only KYC assessment content block (the compliance wall's
 * content half, D5). Present ONLY when the caller's read of
 * `SuitabilityRecordAssessment` succeeds — i.e. for Comply. Desk/Ops
 * always receive `assessmentContent: null`; absence is structural, not a
 * blanked-out rendering.
 */
export const suitabilityAssessmentContentSchema = z.object({
    assessmentId: z.string(),
    /** The SUI-#### source reference, or null. A label — never a derivation. */
    sourceAssessmentRef: z.string().nullable(),
    createdOn: z.string(),
    entityAml: z.record(z.string(), z.unknown()).nullable(),
    health: z.record(z.string(), z.unknown()).nullable(),
    financialInformation: z.record(z.string(), z.unknown()).nullable(),
    assessmentResponses: z.record(z.string(), z.unknown()).nullable(),
    /** How many content rows exist in total (history is append-only). */
    historyCount: z.number().int(),
});
export type SuitabilityAssessmentContent = z.infer<typeof suitabilityAssessmentContentSchema>;

export const suitabilityRecordDetailSchema = z.object({
    recordId: z.string(),
    reference: z.string(),
    assessmentType: suitabilityRecordAssessmentTypeSchema,
    status: suitabilityRecordStatusSchema,
    inceptionDate: z.string().nullable(),
    riskProfileBand: z.string().nullable(),
    factFindId: z.string().nullable(),
    /** The human mandate reference (free text) — independent of the letter. */
    mandateRef: z.string().nullable(),
    internalReviewAssessmentId: z.string().nullable(),
    /** The linked Discretionary Investment Mandate letter (SEP-178). */
    mandateLetterId: z.string().nullable(),
    /** The client that letter is keyed to — the deep-link target, since the
     * mandate surface is `/desk/suitability/mandate/:clientId`. Null whenever
     * `mandateLetterId` is null or the caller cannot read the letter. */
    mandateLetterClientId: z.string().nullable(),
    lastReviewDate: z.string().nullable(),
    nextReviewDate: z.string().nullable(),
    ownerImUserId: z.string(),
    ownerImName: z.string().nullable(),
    // Compliance sign-off group (D6) — read-only for desk/ops.
    approved: z.boolean(),
    approvalDate: z.string().nullable(),
    approvedById: z.string().nullable(),
    approvedByName: z.string().nullable(),
    // Entity identity (non-AML subset, SEP-186). Meaningful only when
    // `assessmentType === 'entity'`; the last three fields are CONDITIONAL on
    // `entityType` (see `entityIdentityConditionalFields`), and the Actions
    // refuse a combination the type does not admit.
    entityName: z.string().nullable(),
    entityType: suitabilityRecordEntityTypeSchema.nullable(),
    /** Free-text qualifier — only when `entityType` is `other`. */
    entityTypeOther: z.string().nullable(),
    entityCreationDate: z.string().nullable(),
    /** trust + corporate. */
    legalEntityIdentifier: z.string().nullable(),
    /** corporate only (no longer an unconditional field). */
    entityRegistrationNumber: z.string().nullable(),
    /** charity only. */
    charityNumber: z.string().nullable(),
    /** Documentary Evidence lineage (UploadBacked, ADR-0018). */
    sourceUploadIds: z.array(z.string()),
    // Per-field ingest provenance (ADR-0015 D8a).
    lastManualEditAt: z.string().nullable(),
    lastIngestAt: z.string().nullable(),
    manuallyEditedFields: z.array(z.string()),
    hasLinkedContact: z.boolean(),
    /** COMPLY ONLY (the wall, D5): null for desk/ops — structurally absent,
     * not blanked. Current = latest by createdOn (answer #13). */
    assessmentContent: suitabilityAssessmentContentSchema.nullable(),
});
export type SuitabilityRecordDetail = z.infer<typeof suitabilityRecordDetailSchema>;

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export const createSuitabilityRecordRequestSchema = z.object({
    /**
     * The owning IM — ONLY honoured for an Ops caller (an explicit UI pick,
     * validated server-side against the active desk roster). For a Desk
     * caller the server resolves the owner from the actor's own book and
     * this field is ignored (ADR-0015 D0.4). The SUR reference is NEVER a
     * request field — the server allocates it (D3).
     */
    ownerImUserId: z.string().optional(),
    assessmentType: suitabilityRecordAssessmentTypeSchema,
    inceptionDate: z.string().optional(),
    riskProfileBand: z.string().optional(),
    mandateRef: z.string().optional(),
    lastReviewDate: z.string().optional(),
    nextReviewDate: z.string().optional(),
    entityName: z.string().optional(),
    entityType: suitabilityRecordEntityTypeSchema.optional(),
    entityTypeOther: z.string().optional(),
    entityCreationDate: z.string().optional(),
    legalEntityIdentifier: z.string().optional(),
    entityRegistrationNumber: z.string().optional(),
    charityNumber: z.string().optional(),
});
export type CreateSuitabilityRecordRequest = z.infer<typeof createSuitabilityRecordRequestSchema>;

// Update shape: every clearable field is `.nullable().optional()` — a clear
// travels as an explicit null (aligned with the update Action input), never
// ''. Reference, owner, status, assessmentType and the approval group are
// deliberately NOT here (their own Actions / never editable).
export const updateSuitabilityRecordRequestSchema = z.object({
    inceptionDate: z.string().nullable().optional(),
    riskProfileBand: z.string().nullable().optional(),
    mandateRef: z.string().nullable().optional(),
    lastReviewDate: z.string().nullable().optional(),
    nextReviewDate: z.string().nullable().optional(),
    entityName: z.string().nullable().optional(),
    entityType: suitabilityRecordEntityTypeSchema.nullable().optional(),
    entityTypeOther: z.string().nullable().optional(),
    entityCreationDate: z.string().nullable().optional(),
    legalEntityIdentifier: z.string().nullable().optional(),
    entityRegistrationNumber: z.string().nullable().optional(),
    charityNumber: z.string().nullable().optional(),
});
export type UpdateSuitabilityRecordRequest = z.infer<typeof updateSuitabilityRecordRequestSchema>;

export const setSuitabilityRecordStatusRequestSchema = z.object({
    action: suitabilityRecordLifecycleActionSchema,
});
export type SetSuitabilityRecordStatusRequest = z.infer<
    typeof setSuitabilityRecordStatusRequestSchema
>;

export const setSuitabilityRecordApprovalRequestSchema = z.object({
    action: suitabilityRecordApprovalActionSchema,
});
export type SetSuitabilityRecordApprovalRequest = z.infer<
    typeof setSuitabilityRecordApprovalRequestSchema
>;

export const reassignSuitabilityRecordOwnerRequestSchema = z.object({
    ownerImUserId: z.string().min(1),
});
export type ReassignSuitabilityRecordOwnerRequest = z.infer<
    typeof reassignSuitabilityRecordOwnerRequestSchema
>;

// ---------------------------------------------------------------------------
// SEP-178 — create the SUR's satellite artefacts in place
// ---------------------------------------------------------------------------

/**
 * One client a Suitability Record is entitled to create an artefact against —
 * resolved SERVER-SIDE from the record's own active `suitability-record →
 * account` relationships, never from a free client search. The Fact
 * Find and the Internal Suitability Assessment are both client-keyed, so
 * "which client?" is the only question a SUR-originated create still has to
 * answer, and the answer set is closed to the record's own parties.
 */
export const suitabilityRecordClientCandidateSchema = z.object({
    clientId: z.string(),
    clientName: z.string().nullable(),
    /** The Account(s) that carried this client onto the record. */
    clientReferences: z.array(z.string()),
    /** A fact find already exists for this client (linking, not creating). */
    hasFactFind: z.boolean(),
});
export type SuitabilityRecordClientCandidate = z.infer<
    typeof suitabilityRecordClientCandidateSchema
>;

export const suitabilityRecordClientCandidatesResponseSchema = z.object({
    recordId: z.string(),
    candidates: z.array(suitabilityRecordClientCandidateSchema),
    /**
     * Linked Accounts that resolve to no single person (joint / multi-owner
     * or unmatched — SEP-166). They cannot key a client-keyed artefact, so
     * they are reported rather than silently dropped.
     */
    unresolvedAccountCount: z.number().int(),
});
export type SuitabilityRecordClientCandidatesResponse = z.infer<
    typeof suitabilityRecordClientCandidatesResponseSchema
>;

/**
 * `clientId` may be omitted when the record resolves to exactly one client;
 * with several it is REQUIRED and must be one of them (the server refuses any
 * other id — a SUR-originated create can never reach outside its own parties).
 */
export const createSuitabilityRecordArtefactRequestSchema = z.object({
    clientId: z.string().optional(),
});
export type CreateSuitabilityRecordArtefactRequest = z.infer<
    typeof createSuitabilityRecordArtefactRequestSchema
>;

/**
 * A new Suitability Record with its first fact find (SEP-353). The IM names the
 * account groups that the record covers, one or more, and the contacts that
 * contribute to it, each with its role on the record. The server opens the
 * record, links each account of each group and each contact, then opens the
 * fact find of the record. The number of contacts makes no second fact find.
 */
export const createSuitabilityRecordWithFactFindRequestSchema = z
    .object({
        assessmentType: suitabilityRecordAssessmentTypeSchema,
        /** The account groups (Clients of Septima) that the record covers. */
        clientIds: z.array(z.string().min(1)).min(1),
        /** The contacts that contribute to the record, with the role of each. */
        contacts: z
            .array(z.object({ contactId: z.string().min(1), roleKey: z.string().min(1) }))
            .default([]),
        /** Ops only: the investment manager who owns the record (D0.4). */
        ownerImUserId: z.string().optional(),
        by: z.string().min(1),
    })
    .refine((d) => new Set(d.clientIds).size === d.clientIds.length, {
        message: 'Name each client account once.',
    })
    .refine((d) => new Set(d.contacts.map((c) => c.contactId)).size === d.contacts.length, {
        message: 'Name each contact once.',
    });
export type CreateSuitabilityRecordWithFactFindRequest = z.input<
    typeof createSuitabilityRecordWithFactFindRequestSchema
>;

const okRecord = z.object({ ok: z.boolean(), recordId: z.string() });
const okCreated = z.object({ ok: z.boolean(), recordId: z.string(), reference: z.string() });
const okRecordWithFactFind = z.object({
    ok: z.boolean(),
    recordId: z.string(),
    reference: z.string(),
    /** The handle of the fact find. Open the fact find with this value. */
    factFindId: z.string(),
});
const okInternalReview = z.object({
    ok: z.boolean(),
    recordId: z.string(),
    internalReviewAssessmentId: z.string(),
    clientId: z.string(),
    created: z.boolean(),
});
const okMandateLetter = z.object({
    ok: z.boolean(),
    recordId: z.string(),
    mandateLetterId: z.string(),
    clientId: z.string(),
    /** False when an existing mandate letter was linked rather than created. */
    created: z.boolean(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const suitabilityRecordsContract = c.router(
    {
        listSuitabilityRecords: {
            method: 'GET',
            path: '/suitability-records',
            query: z
                .object({
                    search: z.string().optional(),
                    status: suitabilityRecordStatusSchema.optional(),
                })
                .optional(),
            responses: { 200: listSuitabilityRecordsResponseSchema },
            summary: 'Suitability Records directory — SUR mandate-container headers',
        },
        getSuitabilityRecord: {
            method: 'GET',
            path: '/suitability-records/:recordId',
            responses: { 200: suitabilityRecordDetailSchema, 404: errorBody },
            summary:
                'Suitability Record detail; assessmentContent is populated for Comply only (the wall, D5)',
        },
        createSuitabilityRecord: {
            method: 'POST',
            path: '/suitability-records',
            body: createSuitabilityRecordRequestSchema,
            responses: { 200: okCreated, 400: errorBody },
            summary:
                'Open a Suitability Record in draft; the SUR reference is server-allocated (D3)',
        },
        updateSuitabilityRecord: {
            method: 'PATCH',
            path: '/suitability-records/:recordId',
            body: updateSuitabilityRecordRequestSchema,
            responses: { 200: okRecord, 400: errorBody, 404: errorBody },
            summary: 'Edit a Suitability Record (update-suitability-record Action)',
        },
        setSuitabilityRecordStatus: {
            method: 'POST',
            path: '/suitability-records/:recordId/status',
            body: setSuitabilityRecordStatusRequestSchema,
            responses: { 200: okRecord, 400: errorBody, 404: errorBody },
            summary: 'Lifecycle a Suitability Record (activate / close / reopen Actions)',
        },
        setSuitabilityRecordApproval: {
            method: 'POST',
            path: '/suitability-records/:recordId/approval',
            body: setSuitabilityRecordApprovalRequestSchema,
            responses: { 200: okRecord, 400: errorBody, 404: errorBody },
            summary:
                'Compliance sign-off (approve / withdraw-approval Actions) — comply only, no lifecycle change (D6)',
        },
        // SEP-178 — create the record's satellite artefacts in place. Desk
        // only: all three artefacts are Desk surfaces keyed by client id.
        listSuitabilityRecordClientCandidates: {
            method: 'GET',
            path: '/suitability-records/:recordId/client-candidates',
            responses: { 200: suitabilityRecordClientCandidatesResponseSchema, 404: errorBody },
            summary:
                'The clients this record links to — the closed answer set for a SUR-originated create (never a free client search)',
        },
        // SEP-353 — a new record, its account and contact links, and its
        // first fact find, in one request.
        createSuitabilityRecordWithFactFind: {
            method: 'POST',
            path: '/suitability-records/with-fact-find',
            body: createSuitabilityRecordWithFactFindRequestSchema,
            responses: { 200: okRecordWithFactFind, 400: errorBody, 404: errorBody },
            summary:
                'Open a Suitability Record over one or more account groups, link its contacts, and open its fact find (SEP-353)',
        },
        createSuitabilityRecordInternalReview: {
            method: 'POST',
            path: '/suitability-records/:recordId/internal-review',
            body: createSuitabilityRecordArtefactRequestSchema,
            responses: { 200: okInternalReview, 400: errorBody, 404: errorBody },
            summary:
                "Open (or link) the Internal Suitability Assessment for one of this record's clients and write internalReviewAssessmentId",
        },
        createSuitabilityRecordMandateLetter: {
            method: 'POST',
            path: '/suitability-records/:recordId/mandate-letter',
            body: createSuitabilityRecordArtefactRequestSchema,
            responses: { 200: okMandateLetter, 400: errorBody, 404: errorBody },
            summary:
                "Open (or link) the Discretionary Investment Mandate letter for one of this record's clients and write mandateLetterId (link-suitability-record-mandate-letter Action)",
        },
        reassignSuitabilityRecordOwner: {
            method: 'POST',
            path: '/suitability-records/:recordId/owner',
            body: reassignSuitabilityRecordOwnerRequestSchema,
            responses: { 200: okRecord, 400: errorBody, 404: errorBody },
            summary:
                "Move a Suitability Record to a different IM's book (ops-mediated, reassign-suitability-record-owner Action)",
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
